import React, { useState } from "react";
import {
  Button,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Space,
  Typography,
  Popconfirm,
  Tooltip,
  message,
} from "antd";
import {
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  inviteCodesService,
  InviteCode,
  CreateInviteCodePayload,
} from "~/services/invite-codes";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Text } = Typography;

const statusColors: Record<string, string> = {
  active: "green",
  exhausted: "default",
  revoked: "red",
  expired: "orange",
};

export const InviteCodesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [form] = Form.useForm();

  const { data: inviteCodes = [], isLoading } = useQuery({
    queryKey: ["invite-codes"],
    queryFn: inviteCodesService.findAll,
  });

  const createMutation = useMutation({
    mutationFn: inviteCodesService.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["invite-codes"] });
      setCreatedCode(data.code);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.message || "Failed to create invite code"
      );
    },
  });

  const revokeMutation = useMutation({
    mutationFn: inviteCodesService.revoke,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invite-codes"] });
      message.success("Invite code revoked");
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.message || "Failed to revoke invite code"
      );
    },
  });

  const handleCreate = (values: any) => {
    const payload: CreateInviteCodePayload = {
      label: values.label || undefined,
      maxUses: values.maxUses || 1,
      expiresAt: values.expiresAt
        ? values.expiresAt.toISOString()
        : undefined,
    };
    createMutation.mutate(payload);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard");
  };

  const getRegisterLink = (code: string) => {
    return `${window.location.origin}/register?inviteCode=${code}`;
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code: string) => (
        <Space>
          <Text code copyable={{ text: code }}>
            {code}
          </Text>
        </Space>
      ),
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (label: string | null) => label || <Text type="secondary">-</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={statusColors[status]}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Usage",
      key: "usage",
      render: (_: any, record: InviteCode) => (
        <Text>
          {record.timesUsed} / {record.maxUses}
        </Text>
      ),
    },
    {
      title: "Expires",
      dataIndex: "expiresAt",
      key: "expiresAt",
      render: (expiresAt: string | null) => {
        if (!expiresAt) return <Text type="secondary">Never</Text>;
        const d = dayjs(expiresAt);
        const isExpired = d.isBefore(dayjs());
        return (
          <Tooltip title={d.format("MMM D, YYYY h:mm A")}>
            <Text type={isExpired ? "danger" : undefined}>
              {isExpired ? "Expired " : ""}
              {d.fromNow()}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => dayjs(date).format("MMM D, YYYY"),
    },
    {
      title: "Created By",
      key: "createdBy",
      render: (_: any, record: InviteCode) =>
        record.createdBy
          ? `${record.createdBy.firstName} ${record.createdBy.lastName}`
          : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: InviteCode) => (
        <Space>
          <Tooltip title="Copy registration link">
            <Button
              size="small"
              icon={<LinkOutlined />}
              onClick={() => copyToClipboard(getRegisterLink(record.code))}
            />
          </Tooltip>
          <Tooltip title="Copy code">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(record.code)}
            />
          </Tooltip>
          {record.status === "active" && (
            <Popconfirm
              title="Revoke this invite code?"
              description="This cannot be undone. The code will no longer work."
              onConfirm={() => revokeMutation.mutate(record.id)}
              okText="Revoke"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Revoke">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <Text strong className="text-lg">
            Invite Codes
          </Text>
          <br />
          <Text type="secondary">
            Generate codes to allow new companies to register on the platform
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setCreatedCode(null);
            setIsModalOpen(true);
          }}
        >
          Generate Code
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={inviteCodes}
        rowKey="id"
        loading={isLoading}
        pagination={{ pageSize: 20 }}
      />

      <Modal
        title="Generate Invite Code"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setCreatedCode(null);
          form.resetFields();
        }}
        footer={
          createdCode
            ? [
                <Button
                  key="close"
                  onClick={() => {
                    setIsModalOpen(false);
                    setCreatedCode(null);
                  }}
                >
                  Close
                </Button>,
              ]
            : undefined
        }
        onOk={() => {
          if (!createdCode) {
            form.submit();
          }
        }}
        okText="Generate"
        confirmLoading={createMutation.isPending}
      >
        {createdCode ? (
          <div className="text-center py-4">
            <Text type="secondary" className="block mb-2">
              Your invite code has been generated:
            </Text>
            <Text
              code
              copyable
              className="text-2xl block mb-4"
            >
              {createdCode}
            </Text>
            <Button
              icon={<LinkOutlined />}
              onClick={() => copyToClipboard(getRegisterLink(createdCode))}
            >
              Copy Registration Link
            </Button>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleCreate}>
            <Form.Item
              name="label"
              label="Label (optional)"
              extra="A note to remember what this code is for"
            >
              <Input placeholder='e.g. "For Brooklyn Art Movers"' />
            </Form.Item>

            <Form.Item
              name="maxUses"
              label="Max Uses"
              initialValue={1}
              extra="How many times this code can be used (1-100)"
            >
              <InputNumber min={1} max={100} className="w-full" />
            </Form.Item>

            <Form.Item
              name="expiresAt"
              label="Expires (optional)"
              extra="Leave empty for no expiration"
            >
              <DatePicker
                showTime
                className="w-full"
                disabledDate={(current) =>
                  current && current < dayjs().startOf("day")
                }
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};
